<template>
  <div class="walls-container enhanced-container" :class="{ 'theme--light': !$vuetify.theme.dark }">
    <!-- Enhanced Background Effects -->
    <div class="background-effects">
      <div class="gradient-orb orb-1"></div>
      <div class="gradient-orb orb-2"></div>
      <div class="gradient-orb orb-3"></div>
    </div>

    <!-- Header Section -->
    <div class="walls-header enhanced-header">
      <v-card class="header-card enhanced-card" elevation="0" :class="{ 'theme--dark': $vuetify.theme.dark }">
        <v-card-text class="header-content">
          <div class="header-icon-wrapper">
            <v-avatar size="100" class="header-icon walls-avatar enhanced-avatar" :color="$vuetify.theme.dark ? 'primary darken-3' : 'primary lighten-4'">
              <v-icon size="50" :color="$vuetify.theme.dark ? 'primary lighten-3' : 'primary'">mdi-heart-multiple</v-icon>
            </v-avatar>
            <div class="icon-pulse-effect"></div>
            <div class="icon-glow-effect"></div>
          </div>
          <h2 class="walls-title enhanced-title">Digital Wall of Memories</h2>
          <p class="walls-description enhanced-description">
            Leave your mark on my portfolio! Share your thoughts, experiences, or just say hello.
            This wall is a space for visitors, clients, and fellow developers to connect.
          </p>
          <div class="stats-banner enhanced-stats">
            <div class="stat-item enhanced-stat-item">
              <div class="stat-number">{{ totalMessages }}</div>
              <div class="stat-label">Messages</div>
            </div>
            <div class="stat-divider enhanced-divider"></div>
            <div class="stat-item enhanced-stat-item">
              <div class="stat-number">{{ uniqueVisitors }}</div>
              <div class="stat-label">Visitors</div>
            </div>
          </div>
        </v-card-text>
      </v-card>
    </div>

    <!-- Sign the Wall Terminal -->
    <div class="sign-wall-section enhanced-section">
      <v-card class="submit-wall-card enhanced-card" elevation="0" :class="{ 'theme--dark': $vuetify.theme.dark }">
        <v-card-text class="submit-wall-content">
          <SubmitWallComponent
            :api-url="apiUrl"
            @message-submitted="onMessageSubmitted"
            @submission-error="onSubmissionError"
          />
        </v-card-text>
      </v-card>
    </div>

    <!-- Messages Wall -->
    <div class="messages-wall enhanced-messages-wall">
      <div class="wall-header enhanced-wall-header">
        <h3 class="wall-title enhanced-wall-title">Messages from Amazing People</h3>
        <div class="title-underline"></div>
      </div>

      <div class="messages-grid enhanced-grid" v-if="filteredMessages.length > 0">
        <v-card
          v-for="message in paginatedMessages"
          :key="message.id || message._id"
          class="message-card enhanced-message-card"
          elevation="0"
          :class="{ 'theme--dark': $vuetify.theme.dark }"
        >
          <v-card-text class="message-card-content">
            <!-- Message Header -->
            <div class="message-header enhanced-message-header">
              <div class="author-info enhanced-author-info">
                <v-avatar size="40" class="author-avatar enhanced-author-avatar" :color="$vuetify.theme.dark ? 'primary darken-2' : 'primary lighten-3'">
                  <v-icon :color="$vuetify.theme.dark ? 'primary lighten-4' : 'primary darken-2'" size="20">mdi-account</v-icon>
                </v-avatar>
                <div class="author-details enhanced-author-details">
                  <div class="author-name enhanced-author-name">{{ message.name }}</div>
                </div>
              </div>
              <div class="message-date enhanced-message-date">
                {{ formatDate(message.createdAt) }}
              </div>
            </div>

            <!-- Message Content -->
            <div class="message-content enhanced-message-content">
              <p class="message-text enhanced-message-text">{{ message.message }}</p>
            </div>

            <!-- Message Footer -->
            <div class="message-footer enhanced-message-footer">
              <div class="message-actions enhanced-actions">
                <v-btn
                  icon
                  small
                  class="action-btn enhanced-action-btn"
                  @click="likeMessage(message.id || message._id)"
                  :color="message.liked ? 'red' : ($vuetify.theme.dark ? 'grey lighten-1' : 'grey darken-1')"
                >
                  <v-icon size="16">{{ message.liked ? 'mdi-heart' : 'mdi-heart-outline' }}</v-icon>
                </v-btn>
                <span class="likes-count enhanced-likes-count">{{ message.likes || 0 }}</span>
              </div>
            </div>
          </v-card-text>

          <!-- Card Enhancement -->
          <div class="message-card-glow enhanced-glow"></div>
        </v-card>
      </div>

      <!-- Empty State -->
      <v-card v-else class="empty-state enhanced-empty-state" elevation="0" :class="{ 'theme--dark': $vuetify.theme.dark }" data-aos="fade-up">
        <v-card-text class="empty-state-content">
          <v-icon size="80" :color="$vuetify.theme.dark ? 'grey lighten-2' : 'grey darken-2'">mdi-message-outline</v-icon>
          <h4 class="empty-state-title">No messages yet</h4>
          <p class="empty-state-text">Be the first to sign the wall!</p>
        </v-card-text>
      </v-card>

      <!-- Load More Button -->
      <div class="load-more-section enhanced-load-more" v-if="hasMoreMessages">
        <v-btn
          color="primary"
          large
          rounded
          class="load-more-btn enhanced-load-more-btn"
          @click="loadMoreMessages"
          :loading="loadingMore"
        >
          <v-icon left>mdi-reload</v-icon>
          Load More Messages
        </v-btn>
      </div>
    </div>

    <!-- Enhanced Success Snackbar -->
    <v-snackbar
      v-model="successSnackbar"
      color="success"
      timeout="4000"
      bottom
      right
      rounded
      class="enhanced-snackbar"
    >
      <div class="snackbar-content enhanced-snackbar-content">
        <v-icon color="white" class="snackbar-icon">mdi-check-circle</v-icon>
        <span>Your message has been added to the wall! 🎉</span>
      </div>
    </v-snackbar>
  </div>
</template>

<script>
import axios from 'axios';
import SubmitWallComponent from '@/components/subcomponents/SubmitWallComponent.vue';
import { getApiUrl } from '@/config/api';

export default {
  name: "WallsComponent",
  components: {
    SubmitWallComponent
  },
  data() {
    return {
      messages: [],
      loadingMessages: true,
      loadingMore: false,
      successSnackbar: false,
      currentPage: 1,
      messagesPerPage: 12,
      totalMessages: 0,
      uniqueVisitors: 0,
      hasMoreMessages: false,
      apiUrl: getApiUrl()
    };
  },
  computed: {
    filteredMessages() {
      // Server-side filtering is handled by the API
      return this.messages;
    },
    paginatedMessages() {
      // Pagination is handled by the API
      return this.messages;
    }
  },
  mounted() {
    this.loadMessages();
  },
  methods: {
    onMessageSubmitted(messageData) {
      // Add new message to the beginning of the array
      this.messages.unshift(messageData);
      this.totalMessages++;

      // Show success message
      this.successSnackbar = true;

      // Refresh statistics
      this.loadMessages(false); // Load without showing loading state
    },

    onSubmissionError(error) {
      console.error('Error submitting message:', error);
      // Handle specific error messages from the API
      if (error.response && error.response.data) {
        alert(`Error: ${error.response.data.error}\nDetails: ${error.response.data.details ? error.response.data.details.join(', ') : error.response.data.message || 'Unknown error'}`);
      } else {
        alert('Failed to submit message. Please try again.');
      }
    },

    async loadMessages(showLoading = true) {
      if (showLoading) this.loadingMessages = true;

      try {
        const params = {
          page: 1,
          limit: this.messagesPerPage,
        };

        const response = await axios.get(`${this.apiUrl}/messages`, { params });

        if (response.data.success) {
          this.messages = response.data.data.messages;
          this.totalMessages = response.data.data.statistics.totalMessages;
          this.uniqueVisitors = response.data.data.statistics.uniqueVisitors;
          this.hasMoreMessages = response.data.data.pagination.hasMore;
          this.currentPage = response.data.data.pagination.currentPage;
        }

      } catch (error) {
        console.error('Error loading messages:', error);
        if (error.code === 'ECONNREFUSED') {
          alert('Cannot connect to server. Please make sure the backend server is running on port 5000.');
        }
      } finally {
        if (showLoading) this.loadingMessages = false;
      }
    },

    async loadMoreMessages() {
      if (!this.hasMoreMessages || this.loadingMore) return;

      this.loadingMore = true;
      try {
        const nextPage = this.currentPage + 1;
        const params = {
          page: nextPage,
          limit: this.messagesPerPage,
        };

        const response = await axios.get(`${this.apiUrl}/messages`, { params });

        if (response.data.success) {
          // Append new messages to existing ones
          this.messages.push(...response.data.data.messages);
          this.hasMoreMessages = response.data.data.pagination.hasMore;
          this.currentPage = nextPage;
        }

      } catch (error) {
        console.error('Error loading more messages:', error);
      } finally {
        this.loadingMore = false;
      }
    },

    async likeMessage(messageId) {
      try {
        const response = await axios.post(`${this.apiUrl}/messages/${messageId}/like`);

        if (response.data.success) {
          // Update the message in the local array
          const message = this.messages.find(msg => (msg.id || msg._id) === messageId);
          if (message) {
            message.likes = response.data.likes;
            message.liked = true; // Mark as liked locally
          }
        }

      } catch (error) {
        console.error('Error liking message:', error);
      }
    },



    formatDate(date) {
      const now = new Date();
      const messageDate = new Date(date);
      const diffTime = Math.abs(now - messageDate);

      const diffSeconds = Math.floor(diffTime / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);
      const diffHours = Math.floor(diffMinutes / 60);
      const diffDays = Math.floor(diffHours / 24);

      // More accurate time formatting
      if (diffSeconds < 60) return 'Just now';
      if (diffMinutes < 60) return `${diffMinutes}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} days ago`;
      if (diffDays < 30) return `${Math.ceil(diffDays / 7)} weeks ago`;
      if (diffDays < 365) return `${Math.ceil(diffDays / 30)} months ago`;
      return `${Math.ceil(diffDays / 365)} years ago`;
    }
  }
};
</script>

<style lang="scss" scoped>
@import "@/styles/variables.scss";

/* Enhanced Container with Theme Awareness */
.walls-container.enhanced-container {
  padding: 2rem 0;
  position: relative;
  overflow: hidden;
  min-height: 100vh;

  // Background gradient with theme awareness
  background: linear-gradient(
    135deg,
    rgba(10, 10, 10, 0.95) 0%,
    rgba(26, 26, 46, 0.95) 50%,
    rgba(22, 33, 62, 0.95) 100%
  );
  backdrop-filter: blur(20px);

  &.theme--light {
    background: linear-gradient(
      135deg,
      rgba(248, 249, 250, 0.95) 0%,
      rgba(240, 242, 247, 0.95) 50%,
      rgba(233, 236, 239, 0.95) 100%
    );
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(72, 149, 239, 0.05));
    z-index: 0;
  }

  &.theme--light::before {
    background: linear-gradient(135deg, rgba(67, 97, 238, 0.03), rgba(72, 149, 239, 0.03));
  }
}

/* Enhanced Background Effects */
.background-effects {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
  z-index: 0;

  .gradient-orb {
    position: absolute;
    border-radius: 50%;
    filter: blur(80px);
    opacity: 0.4;
    animation: float 20s ease-in-out infinite;

    &.orb-1 {
      width: 300px;
      height: 300px;
      background: radial-gradient(circle, rgba(67, 97, 238, 0.3), transparent);
      top: 10%;
      left: 10%;
      animation-delay: 0s;
    }

    &.orb-2 {
      width: 200px;
      height: 200px;
      background: radial-gradient(circle, rgba(233, 30, 99, 0.3), transparent);
      top: 60%;
      right: 20%;
      animation-delay: 7s;
    }

    &.orb-3 {
      width: 250px;
      height: 250px;
      background: radial-gradient(circle, rgba(72, 149, 239, 0.3), transparent);
      bottom: 20%;
      left: 30%;
      animation-delay: 14s;
    }
  }
}

/* Enhanced Header Section */
.walls-header.enhanced-header {
  text-align: center;
  margin-bottom: 4rem;
  padding: 0 2rem;
  position: relative;
  z-index: 1;

  .header-card.enhanced-card {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px !important;
    backdrop-filter: blur(20px);
    padding: 4rem 2rem 3rem 2rem;
    position: relative;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    margin: 0 auto;
    max-width: 900px;

    &.theme--dark {
      background: rgba(255, 255, 255, 0.05) !important;
      border-color: rgba(255, 255, 255, 0.1);
    }

    .theme--light & {
      background: rgba(255, 255, 255, 0.8) !important;
      border-color: rgba(67, 97, 238, 0.1);
      box-shadow: 0 20px 40px rgba(67, 97, 238, 0.1);
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(72, 149, 239, 0.05));
      z-index: 0;
    }

    .header-content {
      position: relative;
      z-index: 1;

      @media (max-width: $breakpoint-xs) {
        padding: 0 1rem;
      }
    }

    @media (max-width: $breakpoint-xs) {
      padding: 2rem 1rem;
      margin: 0 1rem;
    }
  }

  .header-icon-wrapper {
    position: relative;
    display: inline-block;
    margin-bottom: 2rem;

    .walls-avatar.enhanced-avatar {
      position: relative;
      z-index: 3;
      transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
      box-shadow: 0 10px 30px rgba(67, 97, 238, 0.3);

      &:hover {
        transform: scale(1.1) rotate(5deg);
        box-shadow: 0 20px 40px rgba(67, 97, 238, 0.4);
      }
    }

    .icon-pulse-effect {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 140px;
      height: 140px;
      border-radius: 50%;
      background: radial-gradient(circle, rgba(67, 97, 238, 0.2), transparent 70%);
      animation: enhancedPulse 4s ease-in-out infinite;
      z-index: 1;
    }

    .icon-glow-effect {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      width: 180px;
      height: 180px;
      border-radius: 50%;
      background: conic-gradient(from 0deg, transparent, rgba(67, 97, 238, 0.4), rgba(72, 149, 239, 0.4), transparent);
      animation: enhancedRotate 12s linear infinite;
      z-index: 0;
    }
  }

  .walls-title.enhanced-title {
    font-size: 3.5rem;
    font-weight: 900;
    margin: 2rem 0 2.5rem 0;
    padding: 1rem 0;
    background: linear-gradient(135deg, $primary, $accent);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    font-family: 'Poppins', sans-serif;
    text-shadow: 0 4px 20px rgba(67, 97, 238, 0.3);
    position: relative;
    letter-spacing: 0.5px;
    line-height: 1.2;
    text-align: center;

    // Fallback color for browsers that don't support background-clip
    color: #4361EE;

    .theme--light & {
      color: #4361EE;
      text-shadow: 0 2px 10px rgba(67, 97, 238, 0.2);
      // Enhanced contrast for light theme
      background: linear-gradient(135deg, #4361EE, #4895EF);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
    }

    &::after {
      content: '';
      position: absolute;
      bottom: -10px;
      left: 50%;
      transform: translateX(-50%);
      width: 120px;
      height: 4px;
      background: linear-gradient(135deg, $primary, $accent);
      border-radius: 2px;
      box-shadow: 0 2px 10px rgba(67, 97, 238, 0.3);
    }

    @media (max-width: $breakpoint-xs) {
      font-size: 2.5rem;
      margin: 1.5rem 0 2rem 0;
      padding: 0.5rem 0;
      letter-spacing: 0.2px;
    }

    @media (max-width: 480px) {
      font-size: 2.2rem;
      margin: 1rem 0 1.5rem 0;
    }
  }

  .walls-description.enhanced-description {
    max-width: 700px;
    margin: 0 auto 3rem;
    font-size: 1.2rem;
    line-height: 1.8;
    color: rgba(255, 255, 255, 0.9);
    font-weight: 500;

    .theme--light & {
      color: rgba(33, 37, 41, 0.8);
    }
  }

  .stats-banner.enhanced-stats {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 3rem;
    flex-wrap: wrap;
    padding: 2rem;
    background: rgba(255, 255, 255, 0.05);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 20px;
    backdrop-filter: blur(10px);
    max-width: 500px;
    margin: 0 auto;

    .theme--light & {
      background: rgba(255, 255, 255, 0.7);
      border-color: rgba(67, 97, 238, 0.1);
    }

    .stat-item.enhanced-stat-item {
      text-align: center;
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

      &:hover {
        transform: translateY(-3px) scale(1.05);
      }

      .stat-number {
        font-size: 2.5rem;
        font-weight: 900;
        background: linear-gradient(135deg, $primary, $accent);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        background-clip: text;
        line-height: 1;
        text-shadow: 0 2px 10px rgba(67, 97, 238, 0.3);
      }

      .stat-label {
        font-size: 1rem;
        color: rgba(255, 255, 255, 0.8);
        margin-top: 0.5rem;
        font-weight: 600;

        .theme--light & {
          color: rgba(33, 37, 41, 0.7);
        }
      }
    }

    .stat-divider.enhanced-divider {
      width: 2px;
      height: 50px;
      background: linear-gradient(90deg, transparent, rgba(67, 97, 238, 0.5), transparent);
      border-radius: 1px;

      .theme--light & {
        background: linear-gradient(90deg, transparent, rgba(67, 97, 238, 0.3), transparent);
      }

      @media (max-width: $breakpoint-xs) {
        display: none;
      }
    }
  }
}

/* Enhanced Sign Wall Section */
.sign-wall-section.enhanced-section {
  margin-bottom: 4rem;
  padding: 0 2rem;
  position: relative;
  z-index: 1;

  .submit-wall-card.enhanced-card {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 24px !important;
    backdrop-filter: blur(20px);
    position: relative;
    overflow: hidden;
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
    transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

    &:hover {
      transform: translateY(-5px);
      box-shadow: 0 30px 60px rgba(0, 0, 0, 0.15);
    }

    &.theme--dark {
      background: rgba(255, 255, 255, 0.05) !important;
      border-color: rgba(255, 255, 255, 0.1);
    }

    .theme--light & {
      background: rgba(255, 255, 255, 0.8) !important;
      border-color: rgba(67, 97, 238, 0.1);
      box-shadow: 0 20px 40px rgba(67, 97, 238, 0.1);

      &:hover {
        box-shadow: 0 30px 60px rgba(67, 97, 238, 0.15);
      }
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(72, 149, 239, 0.05));
      z-index: 0;
    }

    .submit-wall-content {
      position: relative;
      z-index: 1;
      padding: 2rem;
    }
  }
}

/* Enhanced Messages Wall */
.messages-wall.enhanced-messages-wall {
  position: relative;
  z-index: 1;
  padding: 0 2rem;

  .wall-header.enhanced-wall-header {
    text-align: center;
    margin-bottom: 3rem;
    position: relative;

    .wall-title.enhanced-wall-title {
      font-size: 2.5rem;
      font-weight: 800;
      background: linear-gradient(135deg, $primary, $accent);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-family: 'Poppins', sans-serif;
      margin-bottom: 1rem;
      text-shadow: 0 2px 10px rgba(67, 97, 238, 0.3);

      .theme--light & {
        text-shadow: none;
      }

      @media (max-width: $breakpoint-xs) {
        font-size: 2rem;
      }
    }

    .title-underline {
      width: 80px;
      height: 4px;
      background: linear-gradient(135deg, $primary, $accent);
      margin: 0 auto;
      border-radius: 2px;
      box-shadow: 0 2px 10px rgba(67, 97, 238, 0.3);
    }
  }

  .messages-grid.enhanced-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
    gap: 2rem;
    margin-bottom: 3rem;

    @media (max-width: $breakpoint-xs) {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }
  }

  .message-card.enhanced-message-card {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 20px !important;
    backdrop-filter: blur(15px);
    position: relative;
    overflow: hidden;
    transition: all 0.4s cubic-bezier(0.25, 0.8, 0.25, 1);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);

    &.theme--dark {
      background: rgba(255, 255, 255, 0.05) !important;
      border-color: rgba(255, 255, 255, 0.1) !important;
    }

    .theme--light & {
      background: rgba(255, 255, 255, 0.8) !important;
      border-color: rgba(67, 97, 238, 0.1) !important;
      box-shadow: 0 10px 30px rgba(67, 97, 238, 0.1);
    }

    &:hover {
      transform: translateY(-8px) scale(1.02);
      box-shadow: 0 25px 50px rgba(0, 0, 0, 0.2);

      .theme--light & {
        box-shadow: 0 25px 50px rgba(67, 97, 238, 0.2);
      }
    }

    &::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: linear-gradient(135deg, rgba(67, 97, 238, 0.05), rgba(72, 149, 239, 0.05));
      z-index: 0;
    }

    .message-card-content {
      position: relative;
      z-index: 1;
      padding: 1.5rem;
    }

    .message-card-glow.enhanced-glow {
      position: absolute;
      top: -2px;
      left: -2px;
      right: -2px;
      bottom: -2px;
      background: linear-gradient(45deg, transparent, rgba(67, 97, 238, 0.3), transparent);
      border-radius: 22px;
      opacity: 0;
      z-index: -1;
      transition: opacity 0.4s ease;
    }

    &:hover .message-card-glow.enhanced-glow {
      opacity: 1;
    }

    .message-header.enhanced-message-header {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1.5rem;

      .author-info.enhanced-author-info {
        display: flex;
        align-items: center;
        gap: 1rem;

        .author-avatar.enhanced-author-avatar {
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);
          box-shadow: 0 4px 15px rgba(67, 97, 238, 0.3);

          &:hover {
            transform: scale(1.1) rotate(5deg);
            box-shadow: 0 8px 25px rgba(67, 97, 238, 0.4);
          }
        }

        .author-details.enhanced-author-details {
          .author-name.enhanced-author-name {
            font-weight: 700;
            color: rgba(255, 255, 255, 0.95);
            font-size: 1.1rem;
            font-family: 'Poppins', sans-serif;

            .theme--light & {
              color: rgba(33, 37, 41, 0.9);
            }
          }
        }
      }

      .message-date.enhanced-message-date {
        font-size: 0.85rem;
        color: rgba(255, 255, 255, 0.6);
        font-weight: 500;
        padding: 0.3rem 0.8rem;
        background: rgba(255, 255, 255, 0.1);
        border-radius: 12px;
        backdrop-filter: blur(10px);

        .theme--light & {
          color: rgba(33, 37, 41, 0.6);
          background: rgba(67, 97, 238, 0.1);
        }
      }
    }

    .message-content.enhanced-message-content {
      margin-bottom: 1.5rem;

      .message-text.enhanced-message-text {
        color: rgba(255, 255, 255, 0.9);
        line-height: 1.7;
        font-size: 1rem;
        font-weight: 400;
        margin-bottom: 0;

        .theme--light & {
          color: rgba(33, 37, 41, 0.85);
        }
      }
    }

    .message-footer.enhanced-message-footer {
      display: flex;
      justify-content: flex-start;
      align-items: center;

      .message-actions.enhanced-actions {
        display: flex;
        align-items: center;
        gap: 0.8rem;
        padding: 0.5rem 1rem;
        background: rgba(255, 255, 255, 0.05);
        border-radius: 15px;
        backdrop-filter: blur(10px);
        border: 1px solid rgba(255, 255, 255, 0.1);

        .theme--light & {
          background: rgba(67, 97, 238, 0.1);
          border-color: rgba(67, 97, 238, 0.2);
        }

        .action-btn.enhanced-action-btn {
          transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

          &:hover {
            transform: scale(1.2) rotate(5deg);
          }
        }

        .likes-count.enhanced-likes-count {
          font-size: 0.95rem;
          color: rgba(255, 255, 255, 0.8);
          font-weight: 600;

          .theme--light & {
            color: rgba(33, 37, 41, 0.7);
          }
        }
      }
    }
  }

  .empty-state.enhanced-empty-state {
    background: rgba(255, 255, 255, 0.05) !important;
    border: 1px solid rgba(255, 255, 255, 0.1) !important;
    border-radius: 24px !important;
    backdrop-filter: blur(20px);
    box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);

    &.theme--dark {
      background: rgba(255, 255, 255, 0.05) !important;
      border-color: rgba(255, 255, 255, 0.1) !important;
    }

    .theme--light & {
      background: rgba(255, 255, 255, 0.8) !important;
      border-color: rgba(67, 97, 238, 0.1) !important;
      box-shadow: 0 20px 40px rgba(67, 97, 238, 0.1);
    }

    .empty-state-content {
      text-align: center;
      padding: 4rem 2rem;

      .empty-state-title {
        margin: 1.5rem 0 1rem;
        font-size: 1.8rem;
        font-weight: 700;
        color: rgba(255, 255, 255, 0.8);
        font-family: 'Poppins', sans-serif;

        .theme--light & {
          color: rgba(33, 37, 41, 0.8);
        }
      }

      .empty-state-text {
        font-size: 1.1rem;
        color: rgba(255, 255, 255, 0.6);
        font-weight: 500;

        .theme--light & {
          color: rgba(33, 37, 41, 0.6);
        }
      }
    }
  }

  .load-more-section.enhanced-load-more {
    text-align: center;
    margin-top: 4rem;

    .load-more-btn.enhanced-load-more-btn {
      background: linear-gradient(135deg, $primary, $accent) !important;
      color: white !important;
      font-weight: 600;
      text-transform: none;
      border-radius: 25px !important;
      padding: 16px 40px !important;
      font-size: 1.1rem;
      box-shadow: 0 8px 25px rgba(67, 97, 238, 0.3);
      transition: all 0.3s cubic-bezier(0.25, 0.8, 0.25, 1);

      &:hover {
        transform: translateY(-3px) scale(1.05);
        box-shadow: 0 15px 40px rgba(67, 97, 238, 0.4);
        background: linear-gradient(135deg, $accent, $primary) !important;
      }

      &:disabled {
        opacity: 0.6;
        transform: none;
      }
    }
  }
}

/* Enhanced Snackbar */
.enhanced-snackbar {
  .snackbar-content.enhanced-snackbar-content {
    display: flex;
    align-items: center;
    gap: 1rem;
    font-weight: 600;
    font-size: 1rem;

    .snackbar-icon {
      font-size: 1.3rem;
      filter: drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3));
    }
  }
}

/* Enhanced Animations */
@keyframes enhancedPulse {
  0%, 100% {
    transform: translate(-50%, -50%) scale(1);
    opacity: 0.3;
  }
  50% {
    transform: translate(-50%, -50%) scale(1.2);
    opacity: 0.7;
  }
}

@keyframes enhancedRotate {
  0% {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  100% {
    transform: translate(-50%, -50%) rotate(360deg);
  }
}

@keyframes float {
  0%, 100% {
    transform: translateY(0px) rotate(0deg);
    opacity: 0.4;
  }
  33% {
    transform: translateY(-30px) rotate(120deg);
    opacity: 0.6;
  }
  66% {
    transform: translateY(10px) rotate(240deg);
    opacity: 0.3;
  }
}

/* Enhanced Responsive Design */
@media (max-width: $breakpoint-md) {
  .walls-container.enhanced-container {
    padding: 1rem 0;

    .walls-header.enhanced-header {
      padding: 0 1rem;
      margin-bottom: 3rem;

      .header-card.enhanced-card {
        padding: 2rem 1.5rem;
      }

      .walls-title.enhanced-title {
        font-size: 2.8rem;
      }

      .walls-description.enhanced-description {
        font-size: 1.1rem;
      }
    }

    .sign-wall-section.enhanced-section {
      padding: 0 1rem;

      .submit-wall-card.enhanced-card {
        .submit-wall-content {
          padding: 1.5rem;
        }
      }
    }

    .messages-wall.enhanced-messages-wall {
      padding: 0 1rem;

      .messages-grid.enhanced-grid {
        grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
        gap: 1.5rem;
      }
    }

    .background-effects {
      .gradient-orb {
        filter: blur(60px);

        &.orb-1 {
          width: 200px;
          height: 200px;
        }

        &.orb-2 {
          width: 150px;
          height: 150px;
        }

        &.orb-3 {
          width: 180px;
          height: 180px;
        }
      }
    }
  }
}

@media (max-width: $breakpoint-xs) {
  .walls-container.enhanced-container {
    padding: 0.5rem 0;

    .walls-header.enhanced-header {
      padding: 0 0.5rem;
      margin-bottom: 2rem;

      .header-card.enhanced-card {
        padding: 1.5rem 1rem;
        border-radius: 20px !important;
      }

      .walls-title.enhanced-title {
        font-size: 2.2rem;
      }

      .walls-description.enhanced-description {
        font-size: 1rem;
        margin-bottom: 2rem;
      }

      .stats-banner.enhanced-stats {
        padding: 1.5rem;
        gap: 2rem;

        .stat-item.enhanced-stat-item {
          .stat-number {
            font-size: 2rem;
          }

          .stat-label {
            font-size: 0.9rem;
          }
        }
      }
    }

    .sign-wall-section.enhanced-section {
      padding: 0 0.5rem;
      margin-bottom: 3rem;

      .submit-wall-card.enhanced-card {
        border-radius: 20px !important;

        .submit-wall-content {
          padding: 1rem;
        }
      }
    }

    .messages-wall.enhanced-messages-wall {
      padding: 0 0.5rem;

      .wall-header.enhanced-wall-header {
        margin-bottom: 2rem;

        .wall-title.enhanced-wall-title {
          font-size: 1.8rem;
        }
      }

      .messages-grid.enhanced-grid {
        grid-template-columns: 1fr;
        gap: 1rem;
      }

      .message-card.enhanced-message-card {
        .message-card-content {
          padding: 1rem;
        }

        .message-header.enhanced-message-header {
          margin-bottom: 1rem;

          .author-info.enhanced-author-info {
            gap: 0.8rem;

            .author-details.enhanced-author-details {
              .author-name.enhanced-author-name {
                font-size: 1rem;
              }
            }
          }

          .message-date.enhanced-message-date {
            font-size: 0.8rem;
            padding: 0.2rem 0.6rem;
          }
        }

        .message-content.enhanced-message-content {
          margin-bottom: 1rem;

          .message-text.enhanced-message-text {
            font-size: 0.95rem;
            line-height: 1.6;
          }
        }

        .message-footer.enhanced-message-footer {
          .message-actions.enhanced-actions {
            padding: 0.4rem 0.8rem;
            gap: 0.6rem;

            .likes-count.enhanced-likes-count {
              font-size: 0.9rem;
            }
          }
        }
      }

      .empty-state.enhanced-empty-state {
        border-radius: 20px !important;

        .empty-state-content {
          padding: 3rem 1rem;

          .empty-state-title {
            font-size: 1.5rem;
          }

          .empty-state-text {
            font-size: 1rem;
          }
        }
      }

      .load-more-section.enhanced-load-more {
        margin-top: 3rem;

        .load-more-btn.enhanced-load-more-btn {
          padding: 14px 32px !important;
          font-size: 1rem;
        }
      }
    }

    .background-effects {
      .gradient-orb {
        filter: blur(40px);
        opacity: 0.3;

        &.orb-1 {
          width: 150px;
          height: 150px;
        }

        &.orb-2 {
          width: 120px;
          height: 120px;
        }

        &.orb-3 {
          width: 130px;
          height: 130px;
        }
      }
    }
  }
}

/* Performance Optimizations */
@media (prefers-reduced-motion: reduce) {
  .gradient-orb,
  .icon-pulse-effect,
  .icon-glow-effect {
    animation: none;
  }

  .enhanced-avatar,
  .enhanced-message-card,
  .enhanced-action-btn {
    transition: none;
  }
}
</style>